import { GetServerSidePropsContext } from 'next';
import router from 'next/router';

import {
  AssetsSubType,
  TV_SHOW_PAGE_CONSTANTS,
  TvShowSeoGenreApiParams
} from 'src/utils/constants/TvShowDetailsPageConstants';

import {
  GenreLangFilterType,
  browseByTypeAPICall
} from './GenreLanguageService';
import generateTvShowQueryParams from './TvShowDetailsPageQueryGenerator';
import {
  Maybe,
  TvShowRelatedContent
} from '../../../../src/services/GraphQL/Queries/types';
import { graphQlRequest } from '../../../../src/services/GraphQL/RequestHandler';
import {
  LanguageContent,
  Display
} from '../../../../src/services/responseTypes/singleLaunchResponse';
import SimpleStore from '../../../store/SimpleStore';
import {
  generateCardsUrl,
  navigationHandler
} from '../../../utils/cardsUrlGenerator';
import { DynamicObject } from '../../../utils/commonTypes';
import {
  GET_TV_SHOW_SEO_GENRE_URL,
  LICENSE_URL_FOR_TV_SHOWS_AND_MOVIES,
  ZEETV_STORY_DETAILS_PAGE_API_URL
} from '../../../utils/constants/ApiConstants';
import {
  HTTP_HEADER_KEYS,
  HTTP_METHODS,
  HTTP_URL_PARAMS
} from '../../../utils/constants/HttpMethodsConstants';
import { GENRE_LINK } from '../../../utils/constants/RouterConstants';
import {
  DEVICE_WIDTH_CONSTANTS,
  PAGINATION_ITEMS_LIMIT
} from '../../../utils/constants/utilConstants';
import {
  assetSubTypeBasesMoreLink,
  generateTileForGenreRail,
  getLanguageString
} from '../../../utils/TvShowDetailsHelper/tvShowDetailsHandler';
import { seoPropsGenerator } from '../../../utils/TvShowDetailsHelper/tvShowDetailsSeoGenerator';
import { buildUrlWithQueryParams } from '../../../utils/UrlGenerator';
import { httpService } from '../../fetch/HttpService';
import { TVSHOW_DETAILS_QUERY } from '../../GraphQL/Queries/DetailsPageQueries/TvShowDetailsQuery';

interface TvShowDetailsData {
  tvShowRelatedContent: TvShowRelatedContent;
}

export type GenreRailFormattedData = {
  title: string;
  contents: TvShowRelatedContent[];
  languageCode: string;
  morelink: string;
  genre: string;
};

export const getTvShowDetails = async (id: string, header: string[]) => {
  try {
    const res: TvShowDetailsData = await graphQlRequest(
      TVSHOW_DETAILS_QUERY,
      generateTvShowQueryParams(id),
      'TVSDQ',
      header
    );
    if (!res) return null;
    if (
      res?.tvShowRelatedContent.seasons &&
      Array.isArray(res?.tvShowRelatedContent.seasons) &&
      res?.tvShowRelatedContent.seasons.length > 1
    ) {
      res?.tvShowRelatedContent.seasons?.sort((a, b) => {
        if (a?.orderId && b?.orderId) {
          return b?.orderId - a?.orderId;
        }
        return 0;
      });
    }
    return res?.tvShowRelatedContent;
  } catch (error) {
    // Handle the error here
    httpService.logApiError(error);
  }
  // adding a default value in case if the api fails
  return null;
};

export const navigateLatestEpisodeHandler = (
  tvShowRelatedContent: TvShowRelatedContent
) => {
  const episodeData =
    tvShowRelatedContent &&
    tvShowRelatedContent?.seasons?.length &&
    tvShowRelatedContent.seasons[0]?.episodes?.[0];
  if (episodeData) {
    let slugData = episodeData
      ? generateCardsUrl(episodeData) ?? tvShowRelatedContent?.slug
      : '';
    if (slugData) {
      slugData = slugData.startsWith('/') ? slugData : `/${slugData}`;
    }
    if (slugData) {
      navigationHandler(slugData, (slugData) =>
        router.push(`[...slug]`, `${slugData}`)
      );
    }
  }
};

// used for href in watch latest episode button
export const getLatestEpisodeUrl = (
  tvShowRelatedContent: TvShowRelatedContent
) => {
  const episodeData =
    tvShowRelatedContent &&
    tvShowRelatedContent?.seasons?.length &&
    tvShowRelatedContent.seasons[0]?.episodes?.[0];
  if (episodeData) {
    let slugData = episodeData
      ? generateCardsUrl(episodeData) ?? tvShowRelatedContent?.slug
      : '';
    if (slugData) {
      slugData = slugData.startsWith('/') ? slugData : `/${slugData}`;
    }
    return slugData as string;
  }
  return '';
};

export const getShowType = (showDetails: TvShowRelatedContent) => {
  if (
    Object.keys(showDetails).length &&
    showDetails?.extended?.broadcastState === TV_SHOW_PAGE_CONSTANTS.OFF_AIR
  ) {
    return TV_SHOW_PAGE_CONSTANTS.OFF_AIR;
  }
  return TV_SHOW_PAGE_CONSTANTS.ON_AIR;
};

export const getStoryRailsData = async (id: string) => {
  const url = `${ZEETV_STORY_DETAILS_PAGE_API_URL}/${id}`;
  try {
    const response = await fetch(url, {
      method: HTTP_METHODS.GET
    });
    const data = await response.json();

    // adding a null check for the data
    if (data && Object.keys(data).length) {
      return data;
    }
    return {};
  } catch (error) {
    // Handle the error here
    httpService.logApiError(error);
    return null; // Optionally rethrow the error
  }
};
export const getTvShowGenreUrl = (
  languageCode: string,
  asset_subtype: string
) => {
  const displayLanguage = SimpleStore.get(HTTP_URL_PARAMS.DISPLAY_LANGUAGE);
  const countryCode = SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE);
  const requestObj: TvShowSeoGenreApiParams = {
    page: 1,
    limit: PAGINATION_ITEMS_LIMIT,
    country: countryCode,
    translation: displayLanguage,
    language: languageCode,
    asset_subtype: asset_subtype
  };
  const url = buildUrlWithQueryParams(
    `${GET_TV_SHOW_SEO_GENRE_URL}`,
    requestObj
  );
  return url;
};

export const fetchTvShowGenreData = async (
  languageCode: string,
  asset_subtype: string,
  genres: (string | undefined)[],
  allSupportedLanguages: LanguageContent[],
  allTranslations: DynamicObject,
  headers: string[]
) => {
  try {
    const { LANGUAGES, MOVIE_SEO, tv_shows } = allTranslations || {};

    const url = getTvShowGenreUrl(languageCode, asset_subtype);
    const moreNavMenuLink = assetSubTypeBasesMoreLink(asset_subtype);
    const response = await httpService.fetchApiData({
      url,
      type: HTTP_METHODS.GET,
      headers
    });
    const languageString = getLanguageString(
      languageCode,
      allSupportedLanguages
    );
    // added a null check for genres
    if (genres && genres.length) {
      const filteredGenreData: GenreRailFormattedData[] = genres
        ?.filter(
          (item) =>
            response && Object.keys(response).length && item && item in response
        )
        .map((item) => {
          const genreValue = item?.toLowerCase();
          const formattedGenreForUrl = genreValue?.replace(/[ /]/g, '-');
          const res: GenreRailFormattedData = {
            title: '',
            contents: [],
            morelink: '',
            genre: '',
            languageCode: ''
          };

          const moreLink = `${moreNavMenuLink}${GENRE_LINK}/${formattedGenreForUrl}${
            languageString ? `/${languageString.toLowerCase()}` : ''
          }`;
          const title = generateTileForGenreRail(
            moreLink,
            languageCode,
            LANGUAGES,
            MOVIE_SEO,
            tv_shows
          );

          res.contents = response && item ? response[item] || [] : [];
          res.morelink = moreLink;
          res.genre = genreValue || '';
          res.languageCode = languageCode || '';
          res.title = title || '';

          return res;
        });
      return filteredGenreData;
    }
    return [];
  } catch (error) {
    httpService.logApiError(error);
    return [];
  }
};

export const getLicenseDataForTvShow = async (
  url: string,
  headers: string[]
) => {
  try {
    const response = await httpService.fetchApiData({
      url,
      type: HTTP_METHODS.GET,
      headers
    });
    return response;
  } catch (error) {
    httpService.logApiError(error);
    return null;
  }
};

export const getDetailsPageSeoData = async (
  data: TvShowRelatedContent,
  episodeStrings: DynamicObject,
  context: GetServerSidePropsContext,
  allDisplayLanguages: Display[]
) => {
  const headers = [HTTP_HEADER_KEYS.X_ACCESS_TOKEN];

  const url = `${LICENSE_URL_FOR_TV_SHOWS_AND_MOVIES}${data?.id}`;
  const licenseData = await getLicenseDataForTvShow(url as string, headers);
  const seoData = await seoPropsGenerator({
    detailsData: data as TvShowRelatedContent,
    episodeStrings,
    licenseData,
    context,
    allDisplayLanguages
  });

  return seoData;
};

export const getSubHeaderItems = async (
  deviceWidth: number | undefined,
  assetSubType: Maybe<string> | undefined
) => {
  if ((deviceWidth || 0) < DEVICE_WIDTH_CONSTANTS.deviceWidth1200) {
    return await browseByTypeAPICall(
      assetSubType || AssetsSubType.TVSHOW,
      GenreLangFilterType.genre,
      true
    );
  }
  return [];
};
